import React, {
  createContext,
  useState,
  Dispatch,
  SetStateAction,
  useMemo
} from "react"
import dayjs from "dayjs"
import { noop } from "lib/helpers"
import presets, { DateRangePreset } from "lib/dateRangePresets"

export interface DateRange {
  start: Date
  end: Date
  prevStart?: Date
  prevEnd?: Date
  diffInDays: number
}

interface DatesContextType {
  dateRange: DateRange
  setDateRange: Dispatch<SetStateAction<DateRange>>
  dateRangePresets: ReadonlyArray<DateRangePreset>
  setDateRangePresets: Dispatch<SetStateAction<DateRangePreset[]>>
}

const defaultPreset = presets.find(preset => preset.id === `last-30-days`)

const diff = defaultPreset
  ? dayjs(defaultPreset.end).diff(dayjs(defaultPreset.start), `day`)
  : 30

const defaultRange: DateRange = {
  start:
    defaultPreset?.start ??
    dayjs().subtract(diff, `days`).startOf(`day`).toDate(),
  end: defaultPreset?.end ?? dayjs().endOf(`day`).toDate(),
  diffInDays: diff,
  prevStart: defaultPreset
    ? dayjs(defaultPreset.start).subtract(diff, `days`).toDate()
    : dayjs()
        .subtract(diff * 2, `days`)
        .startOf(`day`)
        .toDate(),
  prevEnd: defaultPreset
    ? dayjs(defaultPreset.end).subtract(diff, `days`).toDate()
    : dayjs().subtract(diff, `days`).endOf(`day`).toDate()
}

export const DatesContext = createContext<DatesContextType>({
  dateRange: defaultRange,
  setDateRange: noop,
  dateRangePresets: presets,
  setDateRangePresets: noop
})

export const DatesProvider = ({
  children,
  initialDateRange = defaultRange,
  initialDateRangePresets = presets
}: {
  children: React.ReactNode
  initialDateRange?: DateRange
  initialDateRangePresets?: DateRangePreset[]
}) => {
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange)
  const [dateRangePresets, setDateRangePresets] = useState<DateRangePreset[]>(
    initialDateRangePresets
  )

  const value = useMemo(
    () => ({ dateRange, setDateRange, dateRangePresets, setDateRangePresets }),
    [dateRange, dateRangePresets]
  )

  return <DatesContext.Provider value={value}>{children}</DatesContext.Provider>
}
